import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import analyze from "rgbaster";
import tinycolor from "tinycolor2";
import { RGBColor } from "d3";
import { takeUntil, tap } from "rxjs/operators";
import { SetupWizardService } from "@services/setup-wizard.service";
import { ElementRef } from "@angular/core";
import { Subject } from "rxjs";
import { NotificationsService } from "@services/notifications.service";
import { TranslationService } from "@services/translation.service";

@Component({
  selector: "app-wizard-theme",
  templateUrl: "./wizard-theme.component.html",
  styleUrls: ["./wizard-theme.component.scss", "../wizard-styles.scss"],
})
export class WizardThemeComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private wizardService: SetupWizardService,
    private notifications: NotificationsService,
    private translationService: TranslationService
  ) {}
  public destroy$: Subject<boolean> = new Subject<boolean>();
  public $primary = "#FFC627";
  public $info = "#001b4e";
  public $button_text_color = "#000000";
  public base64Img;
  public showLogo = false;
  public isPrimaryButtonHover;
  public isProcessing;
  public isLoading;
  public themeInput$;
  public instanceDetails;
  public example1Text;
  public example2Text;
  @ViewChild("logo", { static: false }) logo: ElementRef;

  ngOnInit(): void {
    this.wizardService
      .getInstanceDetails$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.instanceDetails = response;
        this.showLogo = this.instanceDetails.wizard_theme_set === true;
      });

    this.wizardService.getTheme$();
    this.themeInput$ = this.wizardService.themeInput$
      .pipe(takeUntil(this.destroy$))
      .pipe(
        tap(({ isLoading, isProcessing, input }) => {
          const { $primary, $info, $button_text_color } = input as any;
          this.$primary = $primary;
          this.$info = $info;
          this.$button_text_color = $button_text_color;
          this.isProcessing = isProcessing;
          this.isLoading = isLoading;
          this.updateCheckWCAG($button_text_color, $primary, "example1Text");
          this.updateCheckWCAG($primary, $info, "example2Text");
        })
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngAfterViewInit() {
    if (
      this.instanceDetails.wizard_theme_set === true ||
      !this.instanceDetails.hasOwnProperty("wizard_theme_set")
    ) {
      this.logo.nativeElement.style.backgroundImage = `url('/school-assets/school_logo.png')`;
      this.showLogo = true;
    }
  }

  async onFileChanged($event) {
    const maxFileSize = 3 * 1024 * 1024; // 3 MB
    const file = $event?.target?.files?.[0];
    if (!file || file.size >= maxFileSize)
      return this.notifications.popNotification(
        "",
        "Image size must be less than 3MB"
      );
    this.base64Img = await this.fileToBase64(file);
    document.getElementById(
      "wizard-theme-logo-wrapper"
    ).style.backgroundImage = `url(${this.base64Img})`;
    this.showLogo = true;
    if (this.instanceDetails.wizard_theme_set === false) {
      analyze(this.base64Img).then((result) => {
        const $primary = result?.[0]?.color;
        const $info = result?.[1]?.color;
        if ($primary) this.$primary = this.rgbaToHex($primary);
        if ($info) this.$info = this.rgbaToHex($info);
        // if ($primary) this.$primary = $primary;
        // if ($info) this.$info = $info;
        this.updateInput();
        this.updateButtonTextColor();
      });
    }
  }

  updateInput() {
    this.wizardService.updateThemeInput({
      $primary: this.$primary,
      $info: this.$info,
      $button_text_color: this.$button_text_color,
    });

    this.updateCheckWCAG(
      this.$button_text_color,
      this.$primary,
      "example1Text"
    );
    this.updateCheckWCAG(this.$primary, this.$info, "example2Text");
  }

  rgbaToHex(rgba) {
    let match = rgba.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
    );
    function hex(x) {
      return ("0" + parseInt(x).toString(16)).slice(-2);
    }
    return (
      "#" +
      hex(match[1]) +
      hex(match[2]) +
      hex(match[3]) +
      (match[4] ? Math.round(parseFloat(match[4]) * 255).toString(16) : "")
    );
  }

  updateButtonTextColor() {
    const luminance = this.luminance(this.$primary as any);
    this.$button_text_color = luminance >= 0.5 ? "#000000" : "#ffffff";
    this.updateInput();
  }

  removeLogo() {
    this.showLogo = false;
    this.base64Img = "";
    document.getElementById("wizard-theme-logo-wrapper").style.backgroundImage =
      "none";
  }

  fileToBase64(inputFile) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(inputFile);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  updateCheckWCAG(color1, color2, example) {
    this[example] = this.checkWCAG(color1, color2);
  }

  checkWCAG(color1: string, color2: string): string {
    if (!color2 || !color1) return null;
    let contrast = this.contrast(color1, color2) as any;
    if (contrast < 0.22222) {
      return `${this.translationService.getTranslatedContent(
        "passed",
        "Passed"
      )} AAA ${contrast}`;
    }
    if (contrast < 0.33333) {
      return `${this.translationService.getTranslatedContent(
        "passed",
        "Passed"
      )} AA ${contrast}`;
    }
    return `${this.translationService.getTranslatedContent(
      "failed",
      "Failed"
    )} ${contrast}`;
  }

  contrast(
    color1: RGBColor | string,
    color2: RGBColor | string
  ): number | string {
    if (!color1 || !color2) return null;
    let color1rgb, color2rgb;
    if (typeof color1 === "string" && !color1.includes("rgb"))
      color1rgb = this.hexToRgb(color1);
    else color1rgb = color1;
    if (typeof color2 === "string" && !color2.includes("rgb"))
      color2rgb = this.hexToRgb(color2);
    else color2rgb = color2;
    const color1luminance = this.luminance(color1rgb);
    const color2luminance = this.luminance(color2rgb);
    const ratio =
      color1luminance > color2luminance
        ? (color2luminance + 0.05) / (color1luminance + 0.05)
        : (color1luminance + 0.05) / (color2luminance + 0.05);
    return ratio.toFixed(3);
  }
  luminance(color: RGBColor): number {
    if (!color) return null;
    let co = tinycolor(color);
    return co.getLuminance();
  }

  /**
   * Convert hex color to rgb
   * @param hex
   */
  hexToRgb(hex: string): Partial<RGBColor> {
    if (!hex) return null;
    let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (m, r, g, b) {
      return r + r + g + g + b + b;
    });

    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      };
    }
    return null;
  }

  updateTheme() {
    const body = {
      colors: {
        $primary: this.$primary,
        $info: this.$info,
        $button_text_color: this.$button_text_color,
      },
    };
    if (this.base64Img) {
      body["base64Logo"] = this.base64Img.substring(
        this.base64Img.indexOf(",") + 1
      );
    }

    this.wizardService.updateTheme$(body).subscribe();
  }
}
